/**
 * Presentation helpers for WordPress/WooCommerce data.
 */

import type { StorePrices } from './commerce/types';

/**
 * Converts a Store API minor-unit amount to a display string.
 *
 * The store runs THB with `currency_minor_unit: 2`, so `"63000"` is ฿630.00.
 * Amounts arrive as strings and are divided rather than parsed as floats up
 * front, keeping precision intact for the integer part.
 */
export function formatPrice(amount: string | number, prices: StorePrices): string {
  const minor = typeof amount === 'string' ? Number.parseInt(amount, 10) : amount;
  if (!Number.isFinite(minor)) return '';

  const divisor = 10 ** prices.currency_minor_unit;
  const major = minor / divisor;

  // Thai baht is conventionally shown without decimals when whole, which is how
  // the current storefront renders it.
  const hasFraction = minor % divisor !== 0;
  const formatted = major.toLocaleString('en-US', {
    minimumFractionDigits: hasFraction ? prices.currency_minor_unit : 0,
    maximumFractionDigits: prices.currency_minor_unit,
  });

  return `${prices.currency_prefix}${formatted}${prices.currency_suffix}`;
}

/**
 * Renders a variable product's price span, e.g. "฿630 – ฿19,910".
 * Falls back to the single price when the range is absent or degenerate.
 */
export function formatPriceRange(prices: StorePrices): string {
  const range = prices.price_range;
  if (!range) return formatPrice(prices.price, prices);
  if (range.min_amount === range.max_amount) return formatPrice(range.min_amount, prices);
  return `${formatPrice(range.min_amount, prices)} – ${formatPrice(range.max_amount, prices)}`;
}

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  laquo: '«',
  raquo: '»',
  ldquo: '“',
  rdquo: '”',
  lsquo: '‘',
  rsquo: '’',
  reg: '®',
  copy: '©',
  trade: '™',
  deg: '°',
};

/**
 * Decodes the HTML entities WordPress emits in plain-text fields such as
 * `product.name` ("Siam&#8217;s Smile &reg;"). Only needed for values rendered
 * as text — `content.rendered` is already HTML and must not be passed here.
 */
export function decodeEntities(input: string): string {
  if (!input) return '';
  return input
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) =>
      String.fromCodePoint(Number.parseInt(hex, 16)),
    )
    .replace(/&([a-z]+);/gi, (match, name: string) => ENTITIES[name.toLowerCase()] ?? match);
}

/** Strips tags, decodes entities, and collapses whitespace — for meta descriptions. */
export function toPlainText(html: string, maxLength?: number): string {
  const text = decodeEntities(html.replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
  if (!maxLength || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trimEnd()}…`;
}

/**
 * Base used only to parse absolute permalinks for their pathname.
 *
 * A placeholder rather than the real origin, so this module stays free of the
 * private-env import in `commerce/http` and can be used by universal code that
 * also runs in the browser.
 */
const PATH_PARSE_BASE = 'https://origin.invalid';

/**
 * Converts a WordPress permalink into the site-relative path to build.
 *
 * Deriving routes from the live permalink rather than reconstructing them is
 * deliberate: it guarantees the static site reproduces the existing URL
 * structure exactly — including the primary category embedded in product URLs
 * (`/shop/feminised-seeds/siams-smile`) and WPML's `/th` prefix with its
 * translated, percent-encoded category slugs. Getting this wrong would break
 * every indexed URL, which is the least reversible mistake available here.
 *
 * Percent-encoding is decoded so Thai paths are written as real UTF-8.
 */
export function permalinkToPath(permalink: string): string {
  let path: string;
  try {
    path = new URL(permalink, PATH_PARSE_BASE).pathname;
  } catch {
    path = permalink;
  }

  try {
    path = decodeURIComponent(path);
  } catch {
    // Leave malformed sequences as-is rather than failing the build.
  }

  // Normalise to a leading slash and no trailing slash, matching
  // `trailingSlash: 'never'` in the Astro config. The root stays "/".
  path = `/${path.replace(/^\/+/, '').replace(/\/+$/, '')}`;
  return path;
}
